import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { facturationService } from '../../services/api';
import { X } from 'lucide-react';

interface PaiementModalProps {
  factureId: number;
  montantRestant: number;
  // Premier jour accepté (date de facture / de consultation) — `date_paiement_min` de l'API
  dateMin?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PaiementFormData {
  montant: number;
  mode_paiement: string;
  date_paiement: string;
  reference?: string;
  notes?: string;
}

const PaiementModal: React.FC<PaiementModalProps> = ({
  factureId,
  montantRestant,
  dateMin,
  onClose,
  onSuccess
}) => {
  const queryClient = useQueryClient();

  // Récupérer les modes de paiement depuis le backend
  const { data: modesPaiementData, isLoading: loadingModes } = useQuery({
    queryKey: ['modes-paiement'],
    queryFn: () => facturationService.getModesPaiement().then(res => res.data)
  });
  const modesPaiement = modesPaiementData || [];

  const { register, handleSubmit, formState: { errors }, watch } = useForm<PaiementFormData>({
    defaultValues: {
      montant: montantRestant,
      date_paiement: new Date().toISOString().split('T')[0],
      mode_paiement: 'especes'
    }
  });

  const montantSaisi = watch('montant');

  const paiementMutation = useMutation({
    mutationFn: (data: PaiementFormData) =>
      facturationService.ajouterPaiement(factureId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factures'] });
      queryClient.invalidateQueries({ queryKey: ['facture', factureId] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.montant?.[0] ||
                       error.response?.data?.montant ||
                       error.response?.data?.date_paiement?.[0] ||
                       error.response?.data?.date_paiement ||
                       'Erreur lors de l\'enregistrement du paiement';
      alert(errorMsg);
    }
  });

  const onSubmit = async (data: PaiementFormData) => {
    await paiementMutation.mutateAsync(data);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 m-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Ajouter un paiement</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="bg-blue-50 p-3 rounded mb-4">
          <p className="text-sm text-gray-700">
            <span className="font-semibold">Montant restant:</span>{' '}
            <span className="text-blue-600 font-bold">{montantRestant.toLocaleString()} GNF</span>
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant du paiement (GNF) *
            </label>
            <input
              type="number"
              step="0.01"
              {...register('montant', {
                required: 'Le montant est requis',
                min: { value: 0.01, message: 'Le montant doit être supérieur à 0' },
                max: { value: montantRestant, message: `Le montant ne peut pas dépasser ${montantRestant.toLocaleString()} GNF` }
              })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.montant && (
              <p className="text-red-500 text-sm mt-1">{errors.montant.message}</p>
            )}
            {montantSaisi > montantRestant && (
              <p className="text-orange-500 text-sm mt-1">
                ⚠️ Le montant dépasse le solde restant
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mode de paiement *
            </label>
            <select
              {...register('mode_paiement', { required: 'Le mode de paiement est requis' })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              disabled={loadingModes}
            >
              <option value="">Sélectionner...</option>
              {modesPaiement.map((mode) => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
            {errors.mode_paiement && (
              <p className="text-red-500 text-sm mt-1">{errors.mode_paiement.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date du paiement *
            </label>
            <input
              type="date"
              min={dateMin}
              {...register('date_paiement', {
                required: 'La date est requise',
                validate: (v) => !dateMin || v >= dateMin ||
                  `Le paiement ne peut pas être antérieur au ${dateMin.split('-').reverse().join('/')} (consultation / facture)`,
              })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
            />
            {errors.date_paiement && (
              <p className="text-red-500 text-sm mt-1">{errors.date_paiement.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Référence
            </label>
            <input
              {...register('reference')}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="N° de transaction, chèque, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-gray-900"
              placeholder="Notes additionnelles..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={paiementMutation.isPending}
              className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {paiementMutation.isPending ? 'Enregistrement...' : 'Enregistrer le paiement'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 text-gray-700 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaiementModal;
